import React, { useRef, useState, useEffect, useCallback, memo } from "react";
import DOMPurify from "dompurify";

const CellEditor = ({ value, onChange, onSubmit, maxLength, rowData, field, spellCheck = false, onEditStart, onEditEnd }) => {
    const sanitizeForEditing = (raw) => {
        const clean = DOMPurify.sanitize(raw, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

        return clean.replace(/(\r\n|\n|\r)/g, " ");
    };

    const sanitizeForSubmit = useCallback((raw) => {
        const clean = sanitizeForEditing(raw);
        const hasTrailingSpace = /\s$/.test(clean);
        let collapsed = clean.replace(/\s+/g, " ").replace(/^ +/, "");

        if (!hasTrailingSpace) collapsed = collapsed.trimEnd();

        return collapsed;
    }, []);

    const escTriggeredRef = useRef(false);
    const cellFocused = useRef(false);
    const editableRef = useRef(null);
    const [text, setText] = useState(() => sanitizeForEditing(value || ""));
    const [externalValue, setExternalValue] = useState(value);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (value !== externalValue) {
            setExternalValue(value);

            if (document.activeElement !== editableRef.current) {
                const sanitized = sanitizeForEditing(value || "");
                setText(sanitized);

                if (editableRef.current && editableRef.current.textContent !== sanitized) {
                    editableRef.current.textContent = sanitized;
                }
            }
        }
    }, [value, externalValue]);

    useEffect(() => {
        const el = editableRef.current;

        if (el && el.textContent !== text) {
            el.textContent = text;
        }
    }, [text]);

    useEffect(() => {
        if (!isEditing) {
            if (cellFocused.current) {
                onEditEnd?.();
                cellFocused.current = false;
            }
        }
    }, [isEditing]);

    const commitEditingChange = (raw) => {
        let next = sanitizeForEditing(raw);

        if (maxLength && next.length > maxLength) {
            next = next.slice(0, maxLength);
        }

        setText(next);
        onChange && onChange(next);
    };

    const handleInput = (e) => commitEditingChange(e.currentTarget.textContent);

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = (e.clipboardData.getData("text") || "").replace(/(\r\n|\n|\r)/g, " ");
        document.execCommand("insertText", false, pasted);
    };

    const handleBlur = useCallback(() => {
        if (editableRef.current) {
            editableRef.current.blur();
        }

        if (escTriggeredRef.current) {
            escTriggeredRef.current = false;
            setIsEditing(false);
            setText(value);

            return;
        }

        const finalValue = sanitizeForSubmit(text);
        const truncated = maxLength && finalValue.length > maxLength ? finalValue.slice(0, maxLength) : finalValue;

        setText(truncated);
        onChange && onChange(truncated);

        if (value !== truncated) {
            onSubmit && onSubmit(rowData, { id: field, value: truncated });
        }

        setIsEditing(false);
    }, [sanitizeForSubmit, text, maxLength, onChange, value, onSubmit, rowData, field]);

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                escTriggeredRef.current = true;
                handleBlur();
            } else if (e.key === "Enter") {
                e.preventDefault();
                editableRef.current.blur();
            }
        },
        [handleBlur]
    );

    const handleFocus = () => {
        if (isEditing) {
            return;
        }

        setIsEditing(true);
        cellFocused.current = true;
        requestAnimationFrame(() => onEditStart?.());
    };

    return (
        <div
            ref={editableRef}
            contentEditable
            tabIndex={0}
            role="textbox"
            aria-multiline="false"
            suppressContentEditableWarning
            spellCheck={spellCheck}
            autoCorrect="off"
            autoCapitalize="off"
            inputMode="text"
            onInput={handleInput}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{
                outline: "none",
                whiteSpace: isEditing ? "normal" : "pre",
                overflow: isEditing ? "visible" : "hidden",
                textOverflow: isEditing ? "clip" : "ellipsis",
            }}
        />
    );
};

export default memo(CellEditor);
